import getPieveToolCallDisplayText from '@/lib/platform/getPieveToolCallDisplayText.js';
import { isConnectionSetupInteraction } from '@/lib/threadInteractions/connectionPromptVisibility.js';
import resolveToolStepState from './resolveToolStepState.js';
import toConnectionCard from './toConnectionCard.js';

const toText = (item) => {
  if (Array.isArray(item?.lines)) {
    return item.lines.map((line) => (typeof line === 'string' ? line : line?.text || '')).join('\n');
  }
  if (Array.isArray(item?.segments)) {
    return item.segments.map((segment) => segment?.text || '').join('');
  }
  return String(item?.text || '');
};

const isToolStep = (item) => item?.type === 'line' && Boolean(item?.toolName);

// A call that stopped to ask for approval is shown by its approval card, not as a step.
const isAwaitingApproval = (item) => item?.toolArgs?.result?.data?.code === 'AWAITING_APPROVAL';

/**
 * Turns a thread's visible activity into what the thread view draws, in order: the person's
 * requests, Pieve's replies, runs of consecutive tool calls as one trail of steps, approval cards,
 * connection cards (with whether a later run followed them) and short notices (an answered approval,
 * an error). Tool calls hidden from the feed, and calls that
 * stopped to ask for approval (their approval card stands for them), are skipped.
 * `isRunInProgress` tells a tool call without a result whether it is still working or was cut off.
 */
const buildThreadTimeline = ({ activity, isRunInProgress }) => {
  const blocks = [];
  const connectionBlocks = [];

  for (const item of Array.isArray(activity) ? activity : []) {
    // Anything a later run wrote (a new request, the hidden message that answers a card) means the
    // conversation went on after that card.
    for (const block of connectionBlocks) {
      if (item?.runId && block.connection.runId && item.runId !== block.connection.runId) {
        block.conversationMovedOn = true;
      }
    }

    if (!item || item.hiddenInActivityFeed === true || isAwaitingApproval(item)) {
      continue;
    }

    if (isConnectionSetupInteraction(item)) {
      const block = { kind: 'connection', id: item.id, connection: toConnectionCard(item), conversationMovedOn: false };
      connectionBlocks.push(block);
      blocks.push(block);
      continue;
    }

    if (isToolStep(item)) {
      const step = {
        id: item.id,
        label: String((item.completed ? toText(item) : '') || item.displayText || toText(item)).trim() || getPieveToolCallDisplayText({ toolName: item.toolName, args: item.toolArgs }),
        toolName: item.toolName,
        args: item.toolArgs || null,
        state: resolveToolStepState({ item, isRunInProgress })
      };
      const last = blocks[blocks.length - 1];
      if (last?.kind === 'steps') {
        last.steps.push(step);
      } else {
        blocks.push({ kind: 'steps', id: `steps-${item.id}`, steps: [step] });
      }
      continue;
    }

    if (item.type === 'bubble') {
      const text = toText(item).trim();
      const attachments = Array.isArray(item.attachments) ? item.attachments : [];
      if (item.className === 'user') {
        if (text || attachments.length > 0) {
          blocks.push({ kind: 'request', id: item.id, text, attachments, pending: item.pending === true });
        }
      } else if (/^Error: /.test(text)) {
        blocks.push({ kind: 'notice', id: item.id, tone: 'danger', text: text.replace(/^Error: /, '') });
      } else if (text) {
        blocks.push({ kind: 'reply', id: item.id, text });
      }
      continue;
    }

    if (item.type === 'approval') {
      blocks.push({ kind: 'approval', id: item.id, approval: item });
      continue;
    }

    if (item.type === 'line') {
      const text = toText(item).trim();
      if (text) {
        blocks.push({ kind: 'notice', id: item.id, tone: 'neutral', text });
      }
    }
  }

  return blocks;
};

export default buildThreadTimeline;
